// src/pages/documentation/gestion/GestionProductos.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBox,
    faPlus,
    faEdit,
    faTrash,
    faSearch,
    faFilter,
    faTags,
    faImage,
    faQrcode,
    faShare,
    faEye,
    faEyeSlash,
    faExclamationTriangle,
    faArrowRight,
    faArrowLeft,
    faCheckCircle,
    faInfoCircle,
    faLightbulb,
    faLink
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const GestionProductos = () => {
    const [expandedSections, setExpandedSections] = useState({
        introduccion: true,
        crearProducto: true,
        gestionarProductos: true,
        categorias: true,
        herramientas: true
    });

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-purple-600 rounded-lg">
                            <FontAwesomeIcon icon={faBox} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Gestión de Productos</h1>
                            <p className="text-gray-600">Administra tu catálogo de productos e inventario</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/gestion" className="hover:text-gray-700">Gestión</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Productos</span>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué es la Gestión de Productos?</h3>
                            <p className="text-gray-700 mb-3">
                                La gestión de productos te permite administrar todo tu catálogo de productos, controlar el inventario, 
                                organizar productos por categorías y compartir tu catálogo con clientes. Desde aquí puedes crear, editar, 
                                eliminar productos y gestionar sus imágenes, precios y stock.
                            </p>
                            <div className="grid md:grid-cols-3 gap-4 mt-4">
                                <div className="bg-white rounded-lg p-4 border border-purple-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faBox} className="text-purple-600" />
                                        Catálogo
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Crea y gestiona todos tus productos desde un solo lugar
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-purple-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTags} className="text-purple-600" />
                                        Categorías
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Organiza tus productos en categorías y subcategorías
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-purple-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faShare} className="text-purple-600" />
                                        Compartir
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Comparte tu catálogo con clientes mediante QR o enlace
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección: Crear Producto */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('crearProducto')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faPlus} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Crear Productos</h2>
                                <p className="text-sm text-gray-500">Añade nuevos productos a tu catálogo</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.crearProducto ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.crearProducto && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Crear Producto */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPlus} className="text-purple-600" />
                                        1. Crear un Nuevo Producto
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">📍 ¿Dónde crear un producto?</h4>
                                            <p className="text-purple-700 text-sm mb-3">
                                                Ve a la página de <strong>"Inventario"</strong> desde tu dashboard. 
                                                Haz clic en el botón <strong>"Crear Producto"</strong> en la parte superior.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información requerida:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nombre:</strong> Nombre del producto (ej: "Café Latte", "Hamburguesa Clásica")
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Precio:</strong> Precio de venta del producto (puede ser 0 para productos gratuitos)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Descripción:</strong> Descripción detallada del producto (opcional pero recomendado)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Categoría:</strong> Selecciona la categoría a la que pertenece el producto
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información opcional:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Cantidad (Stock):</strong> Cantidad disponible en inventario
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Stock Mínimo:</strong> Cantidad mínima antes de recibir alertas
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Subcategoría:</strong> Subcategoría adicional para mayor organización
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Etiquetas:</strong> Etiquetas personalizadas para búsqueda y filtrado
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Imagen:</strong> Foto del producto (JPG, PNG, GIF - máx. 5MB)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Estado:</strong> Activo o Inactivo (los productos inactivos no aparecen en el catálogo público)
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Mientras más información proporciones del producto, mejor será la experiencia para tus clientes. 
                                                Las imágenes son especialmente importantes para productos que vendes online o en catálogos digitales.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Subir Imagen */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faImage} className="text-purple-600" />
                                        2. Agregar Imagen al Producto
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes agregar una imagen al producto durante la creación o después editándolo.
                                        </p>
                                        
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Requisitos de imagen:</h4>
                                            <ul className="text-purple-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Formatos aceptados:</strong> JPG, JPEG, PNG, GIF</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Tamaño máximo:</strong> 5MB</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Recomendación:</strong> Imágenes de alta calidad (mínimo 500x500 píxeles)</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo agregar imagen:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en el área de carga de imagen o en "Seleccionar imagen"</li>
                                                <li>Elige la imagen desde tu computadora</li>
                                                <li>La imagen se mostrará como vista previa</li>
                                                <li>Si estás creando el producto, la imagen se subirá automáticamente al guardar</li>
                                                <li>Si estás editando, puedes cambiar la imagen en cualquier momento</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> La subida de imágenes puede tardar unos segundos. 
                                                Si hay algún error, puedes intentar subir la imagen más tarde editando el producto.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Gestionar Productos */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('gestionarProductos')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faBox} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Gestionar Productos Existentes</h2>
                                <p className="text-sm text-gray-500">Editar, eliminar y organizar tus productos</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.gestionarProductos ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.gestionarProductos && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Editar Producto */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEdit} className="text-blue-600" />
                                        1. Editar un Producto
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes modificar cualquier información de un producto existente en cualquier momento.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo editar:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la página de Inventario, encuentra el producto que quieres editar</li>
                                                <li>Haz clic en el icono de <strong>lápiz</strong> en la tarjeta del producto</li>
                                                <li>Se abrirá un modal con todos los datos del producto</li>
                                                <li>Modifica los campos que necesites</li>
                                                <li>Guarda los cambios</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Información editable:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Nombre y descripción</li>
                                                <li>• Precio</li>
                                                <li>• Cantidad y stock mínimo</li>
                                                <li>• Categoría y subcategoría</li>
                                                <li>• Etiquetas</li>
                                                <li>• Imagen</li>
                                                <li>• Estado (activo/inactivo)</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Buscar y Filtrar */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faSearch} className="text-blue-600" />
                                        2. Buscar y Filtrar Productos
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            La página de Inventario incluye herramientas de búsqueda y filtrado para encontrar productos rápidamente.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Búsqueda:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Escribe en el campo de búsqueda cualquier término (nombre o descripción)</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>La búsqueda se realiza en tiempo real mientras escribes</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Los resultados se filtran automáticamente</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Filtros disponibles:</h4>
                                            <ul className="text-purple-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Por categoría:</strong> Filtra productos por categoría específica</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Stock bajo:</strong> Muestra solo productos con stock por debajo del mínimo</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Rango de precios:</strong> Filtra por precio mínimo y máximo</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div><strong>Mostrar inactivos:</strong> Incluye productos desactivados en los resultados</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes combinar múltiples filtros para encontrar exactamente lo que buscas. 
                                                Por ejemplo, puedes buscar productos de una categoría específica con stock bajo.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Activar/Desactivar */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEye} className="text-blue-600" />
                                        3. Activar o Desactivar Productos
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes desactivar productos temporalmente sin eliminarlos. Los productos inactivos no aparecen 
                                            en el catálogo público pero permanecen en tu inventario.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo activar/desactivar:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la tarjeta del producto, haz clic en el icono de <strong>ojo</strong> (visible) o <strong>ojo tachado</strong> (oculto)</li>
                                                <li>El estado cambiará inmediatamente</li>
                                                <li>Los productos inactivos aparecen con un indicador visual diferente</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Cuándo usar:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Productos temporalmente fuera de stock</li>
                                                <li>• Productos estacionales fuera de temporada</li>
                                                <li>• Productos que estás probando antes de lanzar</li>
                                                <li>• Productos que quieres ocultar sin perder su información</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Eliminar Producto */}
                                <div className="bg-white border border-red-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                                        4. Eliminar un Producto
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                ⚠️ Advertencia
                                            </h4>
                                            <p className="text-red-700 text-sm mb-2">
                                                Eliminar un producto es una acción <strong>irreversible</strong>. Se eliminarán:
                                            </p>
                                            <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                                                <li>El producto y toda su información</li>
                                                <li>La imagen asociada</li>
                                                <li>El historial de ventas relacionado</li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2">Recomendación:</h4>
                                            <p className="text-yellow-700 text-sm">
                                                En lugar de eliminar, considera <strong>desactivar</strong> el producto. 
                                                Así mantienes la información histórica y puedes reactivarlo en el futuro si es necesario.
                                            </p>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo eliminar:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en el icono de <strong>papelera</strong> en la tarjeta del producto</li>
                                                <li>Confirma la eliminación en el diálogo que aparece</li>
                                                <li>El producto será eliminado permanentemente</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Categorías */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('categorias')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faTags} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Gestionar Categorías</h2>
                                <p className="text-sm text-gray-500">Organiza tus productos en categorías y subcategorías</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.categorias ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.categorias && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Crear Categoría */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPlus} className="text-indigo-600" />
                                        1. Crear una Categoría
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">📍 ¿Dónde crear una categoría?</h4>
                                            <p className="text-indigo-700 text-sm mb-3">
                                                En la página de Inventario, en la sección de "Categorías", haz clic en <strong>"Crear Categoría"</strong>.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información requerida:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nombre:</strong> Nombre de la categoría (ej: "Bebidas", "Comida Rápida", "Postres")
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Descripción:</strong> Descripción opcional de la categoría
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Estado:</strong> Activa o Inactiva (las categorías inactivas no aparecen al crear productos)
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Organiza tus categorías de manera lógica. Por ejemplo, si tienes un restaurante, 
                                                podrías tener categorías como "Entradas", "Platos Principales", "Bebidas", "Postres".
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Editar Categoría */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEdit} className="text-indigo-600" />
                                        2. Editar una Categoría
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes modificar el nombre, descripción y estado de una categoría en cualquier momento.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Cómo editar:</h4>
                                            <ol className="text-indigo-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la sección de Categorías, haz clic en el icono de <strong>lápiz</strong> de la categoría</li>
                                                <li>Modifica los campos que necesites</li>
                                                <li>Guarda los cambios</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> Si cambias el nombre de una categoría, los productos que pertenecen a esa categoría 
                                                se actualizarán automáticamente con el nuevo nombre.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Eliminar Categoría */}
                                <div className="bg-white border border-red-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                                        3. Eliminar una Categoría
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                ⚠️ Advertencia
                                            </h4>
                                            <p className="text-red-700 text-sm mb-2">
                                                Solo puedes eliminar categorías que <strong>no tengan productos asociados</strong>. 
                                                Si una categoría tiene productos, primero debes moverlos a otra categoría o eliminarlos.
                                            </p>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo eliminar:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Asegúrate de que la categoría no tenga productos asociados</li>
                                                <li>Haz clic en el icono de <strong>papelera</strong> de la categoría</li>
                                                <li>Confirma la eliminación</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Herramientas Adicionales */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('herramientas')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faShare} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Herramientas Adicionales</h2>
                                <p className="text-sm text-gray-500">Compartir catálogo y códigos QR</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.herramientas ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.herramientas && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Compartir Catálogo */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faShare} className="text-orange-600" />
                                        1. Compartir Catálogo
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes compartir tu catálogo completo con clientes mediante un enlace o código QR.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo compartir:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la página de Inventario, haz clic en <strong>"Compartir Catálogo"</strong></li>
                                                <li>Se abrirá un modal con opciones de compartir</li>
                                                <li>Puedes copiar el enlace directo o generar un código QR</li>
                                                <li>Comparte el enlace o QR con tus clientes</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Beneficios:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Los clientes pueden ver tu catálogo completo desde cualquier dispositivo</li>
                                                <li>• Puedes imprimir el QR y colocarlo en tu tienda</li>
                                                <li>• El catálogo se actualiza automáticamente cuando agregas o modificas productos</li>
                                                <li>• Solo muestra productos activos</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* QR de Productos */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faQrcode} className="text-orange-600" />
                                        2. Códigos QR de Productos
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Cada producto puede tener su propio código QR que lleva directamente a la página del producto.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo generar QR:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la tarjeta del producto, haz clic en el icono de <strong>QR</strong></li>
                                                <li>Se generará un código QR único para ese producto</li>
                                                <li>Puedes descargar el QR en formato PDF para imprimirlo</li>
                                                <li>Coloca el QR en el producto físico o en materiales de marketing</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Los códigos QR de productos son útiles para menús digitales, etiquetas de productos, 
                                                o para compartir productos específicos en redes sociales.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Control de Stock */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-600" />
                                        3. Control de Stock y Alertas
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes configurar alertas para productos con stock bajo y mantener un control de inventario.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo funciona:</h4>
                                            <ul className="text-orange-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Stock Mínimo:</strong> Define la cantidad mínima antes de recibir alertas
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Filtro de Stock Bajo:</strong> Usa el filtro para ver solo productos con stock bajo
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Actualización Manual:</strong> Actualiza la cantidad cuando recibas nuevos productos
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> El control de stock es manual. Debes actualizar las cantidades 
                                                cuando recibas nuevos productos o cuando se vendan. Si tienes TPV configurado, 
                                                algunas ventas pueden actualizar el stock automáticamente.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/gestion"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Volver a Gestión
                    </Link>
                    <div className="text-sm text-gray-500">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                        Guía completa
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default GestionProductos;

